import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';

const EventContext = createContext();

export const useEvent = () => {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error('useEvent must be used within an EventProvider');
  }
  return context;
};

const isPublicEvent = (event) =>
  event?.isPublished === true && event?.isActive !== false;

export const EventProvider = ({ children }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger / écouter les événements publiés
  useEffect(() => {
    const eventsQuery = query(
      collection(db, 'events'),
      where('isPublished', '==', true)
    );

    const applyDocs = (docs) => {
      const eventsData = docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }))
        .filter(isPublicEvent);
      setEvents(eventsData);
      setError(null);
      setLoading(false);
    };

    const unsubscribe = onSnapshot(
      eventsQuery,
      (snapshot) => applyDocs(snapshot.docs),
      async (snapshotError) => {
        console.error('Erreur listener événements:', snapshotError);
        try {
          const fallback = await getDocs(eventsQuery);
          applyDocs(fallback.docs);
        } catch (fallbackError) {
          console.error('Erreur chargement événements:', fallbackError);
          setEvents([]);
          setError(fallbackError);
          setLoading(false);
        }
      }
    );

    return () => unsubscribe();
  }, []);

  // Obtenir les événements à venir
  const getUpcomingEvents = useCallback(() => {
    const now = new Date();
    return events
      .filter(event => {
        if (!event.isActive && event.isActive !== undefined) return false;
        
        if (event.date) {
          const eventDate = event.date.toDate ? event.date.toDate() : new Date(event.date);
          return eventDate >= now;
        }
        
        return true;
      })
      .sort((a, b) => {
        const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
        const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
        return dateA - dateB;
      });
  }, [events]);

  const value = {
    events,
    getUpcomingEvents,
    loading,
    error
  };

  return (
    <EventContext.Provider value={value}>
      {children}
    </EventContext.Provider>
  );
};

